import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { 
  Download, Search, RefreshCw, Eye, Wallet, 
  Building2, CheckCircle2, AlertCircle, Clock 
} from "lucide-react";
import { format } from "date-fns";

interface SellerPayout {
  seller_id: string;
  business_name: string;
  full_name: string;
  balance_available: number;
  balance_pending: number;
  payout_after_commission: number;
  commission_amount: number;
  bank_account: {
    id: string;
    account_name: string;
    account_number_masked: string;
    account_number_decrypted?: string;
    bank_name: string;
    bank_code: string;
  } | null;
  has_pending_withdrawal: boolean;
}

interface WithdrawalRequest {
  id: string;
  seller_id: string;
  amount: number;
  status: string;
  bank_account_id: string | null;
  created_at: string;
  processed_at: string | null;
  rejection_reason: string | null;
  seller_name: string;
  business_name: string;
}

const COMMISSION_RATE = 0.05; // 5%

export function SellerPayouts() {
  const { toast } = useToast();
  const [payouts, setPayouts] = useState<SellerPayout[]>([]);
  const [withdrawalRequests, setWithdrawalRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPayouts, setSelectedPayouts] = useState<string[]>([]);
  const [selectedPayout, setSelectedPayout] = useState<SellerPayout | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [fetchingBankDetails, setFetchingBankDetails] = useState(false);
  const [pendingPage, setPendingPage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [balancePage, setBalancePage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetchSellerPayouts();
  }, []);

  const fetchSellerPayouts = async () => {
    try {
      setLoading(true);

      // Fetch all pending/recent withdrawal requests directly
      const { data: allWithdrawals, error: withdrawalsError } = await supabase
        .from('withdrawal_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (withdrawalsError) throw withdrawalsError;

      // Get unique seller IDs from withdrawal requests
      const withdrawalSellerIds = [...new Set((allWithdrawals || []).map(w => w.seller_id))];

      // Fetch all escrow accounts with balances
      const { data: escrowAccounts, error: escrowError } = await supabase
        .from('escrow_accounts')
        .select('*')
        .gt('balance_available', 0);

      if (escrowError) throw escrowError;

      const escrowSellerIds = (escrowAccounts || []).map(e => e.seller_id).filter(Boolean);
      
      // Combine all seller IDs
      const allSellerIds = [...new Set([...escrowSellerIds, ...withdrawalSellerIds])];

      if (allSellerIds.length === 0) {
        setPayouts([]);
        setWithdrawalRequests([]);
        setLoading(false);
        return;
      }

      // Fetch seller profiles
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, business_name, full_name')
        .in('id', allSellerIds);

      if (profileError) throw profileError;

      // For admin, fetch all seller bank accounts
      const { data: allBankAccounts, error: allBankError } = await supabase
        .from('bank_accounts')
        .select('id, user_id, account_name, bank_name, bank_code, is_default, status')
        .in('user_id', allSellerIds)
        .eq('status', 'verified');

      // Fetch pending withdrawal requests for escrow-based view
      const pendingWithdrawals = (allWithdrawals || []).filter(w => w.status === 'pending');
      const pendingSellerIds = new Set(pendingWithdrawals.map(w => w.seller_id));

      // Build withdrawal requests with seller info
      const withdrawalData: WithdrawalRequest[] = (allWithdrawals || []).map(w => {
        const profile = profiles?.find(p => p.id === w.seller_id);
        return {
          id: w.id,
          seller_id: w.seller_id,
          amount: Number(w.amount),
          status: w.status,
          bank_account_id: w.bank_account_id,
          created_at: w.created_at,
          processed_at: w.processed_at,
          rejection_reason: w.rejection_reason,
          seller_name: profile?.full_name || 'N/A',
          business_name: profile?.business_name || 'N/A',
        };
      });

      setWithdrawalRequests(withdrawalData);

      // Build payout data from escrow accounts
      if (escrowAccounts && escrowAccounts.length > 0) {
        const payoutData: SellerPayout[] = escrowAccounts.map(escrow => {
          const profile = profiles?.find(p => p.id === escrow.seller_id);
          const bankAccount = allBankAccounts?.find(
            b => b.user_id === escrow.seller_id && (b.is_default || b.status === 'verified')
          );

          const balanceAvailable = Number(escrow.balance_available) || 0;
          const commissionAmount = balanceAvailable * COMMISSION_RATE;
          const payoutAfterCommission = balanceAvailable - commissionAmount;

          return {
            seller_id: escrow.seller_id || '',
            business_name: profile?.business_name || 'N/A',
            full_name: profile?.full_name || 'N/A',
            balance_available: balanceAvailable,
            balance_pending: Number(escrow.balance_pending) || 0,
            payout_after_commission: payoutAfterCommission,
            commission_amount: commissionAmount,
            bank_account: bankAccount ? {
              id: bankAccount.id,
              account_name: bankAccount.account_name,
              account_number_masked: '****' + '****',
              bank_name: bankAccount.bank_name,
              bank_code: bankAccount.bank_code || '',
            } : null,
            has_pending_withdrawal: pendingSellerIds.has(escrow.seller_id || ''),
          };
        });

        setPayouts(payoutData.filter(p => p.balance_available > 0));
      } else {
        setPayouts([]);
      }
    } catch (error) {
      console.error("Error fetching seller payouts:", error);
      toast({
        title: "Error",
        description: "Failed to load seller payouts",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchBankDetailsForExport = async (sellerIds: string[]): Promise<Map<string, any>> => {
    const bankDetailsMap = new Map();
    
    for (const sellerId of sellerIds) {
      const { data: bankAccount } = await supabase
        .from('bank_accounts')
        .select('id')
        .eq('user_id', sellerId)
        .eq('is_default', true)
        .single();

      if (bankAccount) {
        const { data: decrypted, error } = await supabase
          .rpc('admin_get_bank_account_for_withdrawal', {
            p_account_id: bankAccount.id
          });

        if (decrypted && decrypted.length > 0) {
          bankDetailsMap.set(sellerId, decrypted[0]);
        }
      }
    }
    
    return bankDetailsMap;
  };

  const handleViewDetails = async (payout: SellerPayout) => {
    setSelectedPayout(payout);
    setDetailsOpen(true);
    
    if (payout.bank_account) {
      setFetchingBankDetails(true);
      try {
        const { data: decrypted, error } = await supabase
          .rpc('admin_get_bank_account_for_withdrawal', {
            p_account_id: payout.bank_account.id
          });

        if (decrypted && decrypted.length > 0) {
          setSelectedPayout({
            ...payout,
            bank_account: {
              ...payout.bank_account,
              account_number_decrypted: decrypted[0].account_number_decrypted,
              account_name: decrypted[0].account_name,
              bank_code: decrypted[0].bank_code,
              bank_name: decrypted[0].bank_name,
            }
          });
        }
      } catch (error) {
        console.error("Error fetching bank details:", error);
      } finally {
        setFetchingBankDetails(false);
      }
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const eligiblePayouts = filteredPayouts
        .filter(p => p.bank_account && !p.has_pending_withdrawal)
        .map(p => p.seller_id);
      setSelectedPayouts(eligiblePayouts);
    } else {
      setSelectedPayouts([]);
    }
  };

  const handleSelectPayout = (sellerId: string, checked: boolean) => {
    if (checked) {
      setSelectedPayouts([...selectedPayouts, sellerId]);
    } else {
      setSelectedPayouts(selectedPayouts.filter(id => id !== sellerId));
    }
  };

  const exportToPaystackCSV = async () => {
    if (selectedPayouts.length === 0) {
      toast({
        title: "No payouts selected",
        description: "Please select at least one payout to export",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Preparing export...",
      description: "Fetching bank account details",
    });

    try {
      const bankDetailsMap = await fetchBankDetailsForExport(selectedPayouts);

      const csvRowsBankFormat: string[] = [
        "amount,bank_code,account_number,account_name,narration"
      ];

      const selectedPayoutData = payouts.filter(p => selectedPayouts.includes(p.seller_id));

      for (const payout of selectedPayoutData) {
        const bankDetails = bankDetailsMap.get(payout.seller_id);
        
        if (bankDetails) {
          const amountNaira = payout.payout_after_commission.toFixed(2);
          const narration = `Seller payout - ${payout.business_name}`;
          
          csvRowsBankFormat.push(
            `${amountNaira},${bankDetails.bank_code},${bankDetails.account_number_decrypted},${bankDetails.account_name.replace(/,/g, '')},${narration.replace(/,/g, '')}`
          );
        }
      }

      const csvContent = csvRowsBankFormat.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `paystack-payouts-${format(new Date(), 'yyyy-MM-dd-HHmmss')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Export successful",
        description: `Exported ${selectedPayoutData.length} payout(s) for Paystack upload`,
      });
    } catch (error) {
      console.error("Export error:", error);
      toast({
        title: "Export failed",
        description: "Failed to export payout data",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />Pending</Badge>;
      case 'approved':
      case 'processed':
        return <Badge className="gap-1 bg-green-600"><CheckCircle2 className="h-3 w-3" />Processed</Badge>;
      case 'rejected':
        return <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredPayouts = payouts.filter(payout =>
    payout.business_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    payout.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    payout.seller_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pendingWithdrawals = withdrawalRequests.filter(w => w.status === 'pending');

  const totalPayoutAmount = filteredPayouts.reduce((sum, p) => sum + p.payout_after_commission, 0);
  const totalCommission = filteredPayouts.reduce((sum, p) => sum + p.commission_amount, 0);
  const selectedTotal = payouts
    .filter(p => selectedPayouts.includes(p.seller_id))
    .reduce((sum, p) => sum + p.payout_after_commission, 0);

  // Pagination helpers
  const paginate = <T,>(items: T[], page: number) => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return items.slice(start, start + ITEMS_PER_PAGE);
  };
  const totalPages = (count: number) => Math.max(1, Math.ceil(count / ITEMS_PER_PAGE));

  const paginatedPending = paginate(pendingWithdrawals, pendingPage);
  const paginatedHistory = paginate(withdrawalRequests, historyPage);
  const paginatedBalances = paginate(filteredPayouts, balancePage);

  const PaginationControls = ({ page, setPage, total }: { page: number; setPage: (p: number) => void; total: number }) => {
    const pages = totalPages(total);
    if (pages <= 1) return null;
    return (
      <div className="flex items-center justify-between pt-4">
        <p className="text-sm text-muted-foreground">
          Showing {((page - 1) * ITEMS_PER_PAGE) + 1}–{Math.min(page * ITEMS_PER_PAGE, total)} of {total}
        </p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Pending Withdrawals</CardTitle>
            <Clock className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingWithdrawals.length}</div>
            <p className="text-xs text-muted-foreground">
              ₦{pendingWithdrawals.reduce((s, w) => s + w.amount, 0).toLocaleString()} total requested
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Sellers with Balance</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{payouts.length}</div>
            <p className="text-xs text-muted-foreground">Ready for payout</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Platform Commission (5%)</CardTitle>
            <Building2 className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">₦{totalCommission.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">Revenue to platform</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Payout</CardTitle>
            <Download className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">₦{totalPayoutAmount.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">After 5% commission</p>
          </CardContent>
        </Card>
      </div>

      {/* Pending Withdrawal Requests */}
      {pendingWithdrawals.length > 0 && (
        <Card className="border-orange-200">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-orange-500" />
              <div>
                <CardTitle>Pending Withdrawal Requests</CardTitle>
                <CardDescription>
                  Sellers requesting payout of their available balance
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Seller</TableHead>
                  <TableHead className="text-right">Amount Requested</TableHead>
                  <TableHead>Requested At</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedPending.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{request.business_name}</p>
                        <p className="text-xs text-muted-foreground">{request.seller_name}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      ₦{request.amount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(request.created_at), 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(request.status)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <PaginationControls page={pendingPage} setPage={setPendingPage} total={pendingWithdrawals.length} />
          </CardContent>
        </Card>
      )}

      {/* All Withdrawal Requests History */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle>All Withdrawal Requests</CardTitle>
              <CardDescription>
                Complete history of seller withdrawal requests
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchSellerPayouts}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : withdrawalRequests.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No withdrawal requests yet
            </div>
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Seller</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Processed</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedHistory.map((request) => (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{request.business_name}</p>
                        <p className="text-xs text-muted-foreground">{request.seller_name}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      ₦{request.amount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(request.created_at), 'MMM dd, yyyy HH:mm')}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {request.processed_at 
                        ? format(new Date(request.processed_at), 'MMM dd, yyyy HH:mm')
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(request.status)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                      {request.rejection_reason || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <PaginationControls page={historyPage} setPage={setHistoryPage} total={withdrawalRequests.length} />
            </>
          )}
        </CardContent>
      </Card>

      {/* Seller Balances & Export Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle>Seller Balances</CardTitle>
              <CardDescription>
                Review and export seller payment details for Paystack
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search sellers..."
                  className="pl-10 w-64"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button variant="outline" onClick={fetchSellerPayouts}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <Button 
                onClick={exportToPaystackCSV}
                disabled={selectedPayouts.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export for Paystack ({selectedPayouts.length})
              </Button>
            </div>
          </div>
          {selectedPayouts.length > 0 && (
            <div className="mt-4 p-3 bg-primary/10 rounded-lg">
              <p className="text-sm font-medium">
                Selected: {selectedPayouts.length} seller(s) | 
                Total payout: <span className="text-primary">₦{selectedTotal.toLocaleString()}</span>
              </p>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading seller payouts...</div>
          ) : filteredPayouts.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No sellers with available balance for payout
            </div>
          ) : (
            <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedPayouts.length === filteredPayouts.filter(p => p.bank_account && !p.has_pending_withdrawal).length && filteredPayouts.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Bank Account</TableHead>
                  <TableHead className="text-right">Available Balance</TableHead>
                  <TableHead className="text-right">Commission (5%)</TableHead>
                  <TableHead className="text-right">Payout Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedBalances.map((payout) => (
                  <TableRow key={payout.seller_id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedPayouts.includes(payout.seller_id)}
                        onCheckedChange={(checked) => handleSelectPayout(payout.seller_id, !!checked)}
                        disabled={!payout.bank_account || payout.has_pending_withdrawal}
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{payout.business_name}</p>
                        <p className="text-xs text-muted-foreground">{payout.full_name}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {payout.bank_account ? (
                        <div>
                          <p className="font-medium">{payout.bank_account.bank_name}</p>
                          <p className="text-xs text-muted-foreground">{payout.bank_account.account_name}</p>
                        </div>
                      ) : (
                        <Badge variant="outline" className="text-destructive">
                          No bank account
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      ₦{payout.balance_available.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono text-green-600">
                      ₦{payout.commission_amount.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      ₦{payout.payout_after_commission.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {payout.has_pending_withdrawal ? (
                        <Badge variant="secondary" className="gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Pending Request
                        </Badge>
                      ) : payout.bank_account ? (
                        <Badge variant="default" className="gap-1 bg-green-600">
                          <CheckCircle2 className="h-3 w-3" />
                          Ready
                        </Badge>
                      ) : (
                        <Badge variant="destructive">Missing Bank</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(payout)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <PaginationControls page={balancePage} setPage={setBalancePage} total={filteredPayouts.length} />
            </>
          )}
        </CardContent>
      </Card>

      {/* Payout Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Payout Details</DialogTitle>
            <DialogDescription>
              Full details for seller payout
            </DialogDescription>
          </DialogHeader>
          {selectedPayout && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Business Name</p>
                  <p className="font-semibold">{selectedPayout.business_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Full Name</p>
                  <p>{selectedPayout.full_name}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Seller ID</p>
                  <p className="font-mono text-xs">{selectedPayout.seller_id}</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Bank Account Details</h4>
                {selectedPayout.bank_account ? (
                  <div className="grid grid-cols-2 gap-4 bg-muted/50 p-4 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Bank Name</p>
                      <p className="font-semibold">{selectedPayout.bank_account.bank_name}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Bank Code</p>
                      <p className="font-mono">{selectedPayout.bank_account.bank_code || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Account Name</p>
                      <p>{selectedPayout.bank_account.account_name}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Account Number</p>
                      {fetchingBankDetails ? (
                        <p className="text-muted-foreground">Loading...</p>
                      ) : (
                        <p className="font-mono font-semibold">
                          {selectedPayout.bank_account.account_number_decrypted || '****'}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-destructive/10 p-4 rounded-lg text-center">
                    <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
                    <p className="text-destructive font-medium">No bank account on file</p>
                    <p className="text-sm text-muted-foreground">Seller needs to add bank details</p>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Payout Breakdown</h4>
                <div className="space-y-2 bg-muted/50 p-4 rounded-lg">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Available Balance</span>
                    <span className="font-mono">₦{selectedPayout.balance_available.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <span>Platform Commission (5%)</span>
                    <span className="font-mono">- ₦{selectedPayout.commission_amount.toLocaleString()}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-semibold text-lg">
                    <span>Net Payout</span>
                    <span className="font-mono text-primary">₦{selectedPayout.payout_after_commission.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {selectedPayout.balance_pending > 0 && (
                <div className="bg-yellow-500/10 p-3 rounded-lg">
                  <p className="text-sm text-yellow-700">
                    <AlertCircle className="h-4 w-4 inline mr-1" />
                    Additional ₦{selectedPayout.balance_pending.toLocaleString()} pending release
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
